// src/ui/screens/Measurement.tsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MeasurementPipeline,
  type PipelineState,
  type FrameUpdate,
} from "../../services/pipeline/MeasurementPipeline";
import { loadStoredAnthropometry } from "./Onboarding";
import { db } from "../../storage/db";
import { colors } from "../design-system/colors";
import { fontFamily, typeScale } from "../design-system/typography";
import { space, radius, minTapTarget } from "../design-system/spacing";
import ARSkeletonOverlay from "../components/ARSkeletonOverlay";
import XRayOverlay from "../components/XRayOverlay";
import RiskGauge from "../components/RiskGauge";
import { track } from "../../services/analytics";

const STATE_MESSAGES: Record<PipelineState, string | null> = {
  idle: null,
  requesting_camera: "カメラへのアクセスを許可してください",
  camera_ready: "フレーム内に立ってください",
  no_person_detected: "フレーム内に人が検出できません。位置を調整してください",
  multi_person_detected: "複数人検出。一番大きく映っている人を対象にしています",
  low_light_warning: "少し暗いようです。照明を確認してください",
  measuring: null,
  generating_advice: "アドバイスを生成しています…",
  error: "計測中にエラーが発生しました",
};

export default function Measurement() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const pipelineRef = useRef<MeasurementPipeline | null>(null);

  const [exerciseType, setExerciseType] = useState<"DEADLIFT" | "SQUAT">("DEADLIFT");
  const [pipelineState, setPipelineState] = useState<PipelineState>("idle");
  const [latestFrame, setLatestFrame] = useState<FrameUpdate | null>(null);
  const [liveKeypoints, setLiveKeypoints] = useState<{
    flat: Float32Array;
    width: number;
    height: number;
  } | null>(null);
  const [xrayMode, setXrayMode] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState<number | null>(null);

  useEffect(() => {
    const pipeline = new MeasurementPipeline({
      onStateChange: setPipelineState,
      onFrameUpdate: (update) => setLatestFrame(update),
      onVisionFrame: (flat, width, height) => setLiveKeypoints({ flat, width, height }),
      onAdviceReady: async (advice) => {
        await finalizeSession(advice);
      },
      onError: (message) => {
        console.error("[Measurement] pipeline error:", message);
      },
    });
    pipelineRef.current = pipeline;

    const anthro = loadStoredAnthropometry();
    void pipeline.initialize({
      heightM: (anthro?.heightCm ?? 170) / 100,
      massKg: anthro?.massKg ?? 65,
      sex: anthro?.sex ?? "male",
      externalLoadKg: 0, // MVP: 外部負荷はSettingsで将来設定可能にする(15章の拡張候補)
      // modelBaseUrl省略: MediaPipe/WebLLMは既定で公式CDNから取得する
      // (自前ホストしたい場合はpublic/models/README.md参照のうえ"/models"等を指定)
    });

    return () => pipeline.dispose();
  }, []);

  const handleToggleXray = () => {
    // Next Gen Award向け: Xレイモードは審査員・利用者が誰でも即座に確認できる
    // ショーケース機能として無償公開する(課金ゲートは設けない)。
    setXrayMode((v) => !v);
    track("xray_mode_toggled", { enabled: !xrayMode });
  };

  const handleStartStop = async () => {
    if (!isRunning) {
      if (videoRef.current) {
        await pipelineRef.current?.startCamera(videoRef.current);
      }
      setIsRunning(true);
      setSessionStartedAt(Date.now());
      track("measurement_started", { exerciseType });
    } else {
      pipelineRef.current?.stop();
      setIsRunning(false);
      pipelineRef.current?.finishSession({
        exerciseType,
        repCount: estimateRepCount(),
        overallScore: latestFrame?.biomech.overall_score ?? 100,
        riskLevel: latestFrame?.biomech.risk_level ?? "safe",
      });
    }
  };

  const estimateRepCount = () => {
    // MVPの簡易実装: 圧縮力の極小値検出によるレップカウントは今後実装(15章)。
    // 現状は経過時間からの粗い推定値を返す。
    if (!sessionStartedAt) return 0;
    const elapsedSec = (Date.now() - sessionStartedAt) / 1000;
    return Math.max(1, Math.round(elapsedSec / 4));
  };

  const finalizeSession = async (advice: string) => {
    const sessionId = crypto.randomUUID();
    const now = Date.now();
    const metrics = pipelineRef.current?.getRepSummaryArrays() ?? {
      compression: [],
      shear: [],
      kneeVarus: [],
    };
    const max = (arr: number[]) => (arr.length ? Math.max(...arr) : 0);

    await db.sessions.add({
      id: sessionId,
      exerciseType,
      startedAt: sessionStartedAt ?? now,
      endedAt: now,
      frameCount: metrics.compression.length,
      deviceInfo: navigator.userAgent.slice(0, 120),
      isSynced: false,
    });
    await db.resultSummaries.add({
      sessionId,
      overallScore: latestFrame?.biomech.overall_score ?? 100,
      maxShearForce: max(metrics.shear),
      maxCompressionForce: max(metrics.compression),
      riskLevel: latestFrame?.biomech.risk_level ?? "safe",
      slmAdviceText: advice,
    });

    track("measurement_completed", { exerciseType });
    navigate(`/result/${sessionId}`);
  };

  const message = STATE_MESSAGES[pipelineState];
  const showOverlayWarning =
    pipelineState === "no_person_detected" ||
    pipelineState === "multi_person_detected" ||
    pipelineState === "low_light_warning";

  return (
    <div style={styles.container}>
      <div style={styles.videoWrap}>
        <video ref={videoRef} muted playsInline style={styles.video} />
        {(liveKeypoints || latestFrame) && (
          <>
            {!xrayMode && (
              <ARSkeletonOverlay keypoints={liveKeypoints} />
            )}
            {xrayMode && latestFrame && (
              <XRayOverlay
                data={{
                  width: videoRef.current?.videoWidth ?? 640,
                  height: videoRef.current?.videoHeight ?? 480,
                  hipScreen: { x: (videoRef.current?.videoWidth ?? 640) / 2, y: (videoRef.current?.videoHeight ?? 480) / 2 },
                  shoulderScreen: { x: (videoRef.current?.videoWidth ?? 640) / 2, y: 80 },
                  hipCovTrace: latestFrame.biomech.l4l5_uncertainty,
                  biomech: latestFrame.biomech,
                }}
              />
            )}
          </>
        )}

        {showOverlayWarning && message && (
          <div style={styles.warningBanner}>{message}</div>
        )}
      </div>

      <div style={styles.controls}>
        {!isRunning && (
          <div style={styles.exerciseToggle}>
            {(["DEADLIFT", "SQUAT"] as const).map((ex) => (
              <button
                key={ex}
                onClick={() => setExerciseType(ex)}
                style={{
                  ...styles.exerciseButton,
                  borderColor: exerciseType === ex ? colors.amber : colors.surfaceBorder,
                  color: exerciseType === ex ? colors.amber : colors.textSecondary,
                }}
              >
                {ex === "DEADLIFT" ? "デッドリフト" : "スクワット"}
              </button>
            ))}
          </div>
        )}

        {isRunning && latestFrame && (
          <div style={styles.liveReadoutWrap}>
            <RiskGauge
              riskLevel={latestFrame.biomech.risk_level}
              compressionN={latestFrame.biomech.l4l5_compression_n}
              shearN={latestFrame.biomech.l4l5_shear_n}
            />
          </div>
        )}

        <div style={styles.bottomRow}>
          <button onClick={handleToggleXray} style={styles.xrayButton} aria-pressed={xrayMode}>
            {xrayMode ? "Xレイ ON" : "Xレイモード"}
          </button>

          <button
            onClick={handleStartStop}
            style={{
              ...styles.mainButton,
              background: isRunning ? colors.danger : colors.amber,
            }}
          >
            {isRunning ? "計測 停止" : "計測 開始"}
          </button>

          <div style={{ width: 96 }} />
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    background: colors.bg,
    fontFamily: fontFamily.ui,
  },
  videoWrap: {
    position: "relative",
    flex: 1,
    background: "#000",
    overflow: "hidden",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scaleX(-1)", // セルフィー視点の鏡像表示
  },
  warningBanner: {
    position: "absolute",
    top: space.md,
    left: space.md,
    right: space.md,
    padding: space.sm,
    borderRadius: radius.md,
    background: "rgba(11,15,25,0.85)",
    color: colors.caution,
    fontSize: typeScale.caption.size,
    textAlign: "center",
  },
  controls: {
    padding: space.lg,
    display: "flex",
    flexDirection: "column",
    gap: space.md,
  },
  exerciseToggle: { display: "flex", gap: space.sm },
  exerciseButton: {
    flex: 1,
    minHeight: minTapTarget,
    borderRadius: radius.md,
    border: "1.5px solid",
    background: "transparent",
    fontFamily: fontFamily.ui,
    fontSize: typeScale.body.size,
  },
  liveReadoutWrap: { marginBottom: space.xs },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.md,
  },
  xrayButton: {
    minHeight: minTapTarget,
    padding: `0 ${space.md}`,
    borderRadius: radius.full,
    border: `1.5px solid ${colors.surfaceBorder}`,
    background: colors.surface,
    color: colors.textSecondary,
    fontFamily: fontFamily.ui,
    fontSize: typeScale.caption.size,
    width: 96,
  },
  mainButton: {
    flex: 1,
    minHeight: 64,
    borderRadius: radius.full,
    border: "none",
    color: colors.bg,
    fontSize: typeScale.h1.size,
    fontWeight: 700,
    fontFamily: fontFamily.ui,
    cursor: "pointer",
  },
};
